// src/chat/ChatAssistant.jsx
/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Auth } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css'; // ★ 必须：KaTeX 样式
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import * as prismStyles from 'react-syntax-highlighter/dist/esm/styles/prism';
import toocanImage from '../assets/img/toocan.png';
// ★ 3D 角色浮层已停用，改回图标浮层提醒（Fab），保留原实现待用
// import { AlphaAgent } from '../vendor/alpha-3d.esm.js';

// ===== 速度参数 =====
const PACE_CPS = 40;
const FIRST_TOKEN_DELAY_MS = 200;

// 生成失败时的友好提示（后端会把 assistant 消息置为 FAILED / 'Generation failed.'）
const FAILED_ASSISTANT_MESSAGE =
  'Sorry, I could not generate a reply just now. Please try again in a moment.';

// ===== AppSync 端点 =====
const GRAPHQL_ENDPOINT = process.env.REACT_APP_CHAT_GRAPHQL_ENDPOINT;
if (!GRAPHQL_ENDPOINT) console.warn('ChatAssistant: missing REACT_APP_CHAT_GRAPHQL_ENDPOINT');

// ===== GraphQL =====
const MESSAGES_BY_CONV = `
  query MessagesByConversation($conversationId: ID!, $limit: Int, $nextToken: String) {
    messagesByConversation(
      conversationId: $conversationId,
      sortDirection: ASC,
      limit: $limit,
      nextToken: $nextToken
    ) {
      items { id role content status createdAt updatedAt }
      nextToken
    }
  }
`;
const SEND_MESSAGE = `
  mutation SendMessage($conversationId: ID!, $content: String!, $background: String) {
    sendMessage(conversationId: $conversationId, content: $content, background: $background) {
      id role content status createdAt updatedAt conversationId
    }
  }
`;

async function callGraphQL(query, variables) {
  const session = await Auth.currentSession();
  const jwt = session.getIdToken().getJwtToken();
  const res = await fetch(GRAPHQL_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: jwt },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.errors) throw new Error(JSON.stringify(data.errors));
  return data.data;
}

function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
}
const ts = () => new Date().toISOString();

// ===== Markdown 渲染 =====
function Markdown({ children }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        code({ inline, className, children: codeChildren, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          const code = String(codeChildren).replace(/\n$/, '');
          if (inline) return <CodeInline {...props}>{code}</CodeInline>;
          return (
            <SyntaxHighlighter
              style={prismStyles.oneLight}
              language={match ? match[1] : 'text'}
              PreTag="div"
              {...props}
            >
              {code}
            </SyntaxHighlighter>
          );
        },
        p({ children: pChildren }) {
          return <P>{pChildren}</P>;
        },
        a({ href, children: aChildren }) {
          return (
            <a href={href} target="_blank" rel="noreferrer">
              {aChildren}
            </a>
          );
        },
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

// ===== 底部判断（两行以内）=====
function getLineHeightPx(el) {
  if (!el) return 22;
  const lh = window.getComputedStyle(el).lineHeight;
  const n = parseFloat(lh);
  return Number.isFinite(n) ? n : 22;
}
function isNearBottom(el, lines = 2) {
  if (!el) return true;
  const { scrollTop, scrollHeight, clientHeight } = el;
  const distance = scrollHeight - scrollTop - clientHeight;
  return distance <= getLineHeightPx(el) * lines;
}

// ===== 主组件 =====
export default function ChatAssistant({
  isOpen,
  onClose,
  onOpen,
  questionObj,
  getCorrect,
  getWrongs,
  selectedAnswer,
  passage,
  autoSendFirstMessage,
}) {
  const [messages, setMessages] = useState([]);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [input, setInput] = useState('');
  const [minimized, setMinimized] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  
  // 窗口尺寸和位置状态
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [windowPosition, setWindowPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  // 自动滚动控制 & “回到底部”按钮
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [atBottom, setAtBottom] = useState(true); // ★ 新增：是否在底部附近
  const showScrollBtn = !atBottom; // 不在底部就显示按钮

  // 打字机（仅最后一条 AI）
  const [typingMsgId, setTypingMsgId] = useState(null);
  const shownTextRef = useRef('');
  const targetTextRef = useRef('');
  const typingTimerRef = useRef(null);
  const firstTokenTimerRef = useRef(null);

  const firstOpen = useRef(true);
  const convIdRef = useRef(null);
  const pollTimer = useRef(null);
  const chatBodyRef = useRef(null);
  const sendingRef = useRef(false);           // 发送中的短暂窗口，暂停用轮询结果覆盖
  const pendingServerSnapRef = useRef(null);  // 若发送期间收到了服务器快照，先缓存
  const panelRef = useRef(null);
  const headerRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const hasHistoryMessagesRef = useRef(false); // 记录是否已有历史消息
  // ★ 3D 角色浮层相关 ref（已停用）
  // const agentRef = useRef(null);              // 3D 角色实例
  // const stateLoopTimerRef = useRef(null);     // 动作交替定时器
  // const chatUiStateRef = useRef({ isOpen: false, minimized: false, onOpen: null, onClose: null });
  // const modalStateRef = useRef({
  //   isOpen: false,
  //   backdrop: null,
  //   keydownHandler: null,
  //   originalSize: { width: 220, height: 240 },
  //   currentSize: { width: 220, height: 240 },
  //   pausedLoop: false,
  // });

  /* ★ 3D 角色浮层已停用（改回图标浮层提醒 Fab），以下代码整段保留待用
  // 同步 UI 状态到 ref（供 3D agent 回调读取，避免陈旧闭包）
  useEffect(() => {
    chatUiStateRef.current.isOpen = isOpen;
    chatUiStateRef.current.minimized = minimized;
    chatUiStateRef.current.onOpen = onOpen;
    chatUiStateRef.current.onClose = onClose;
  }, [isOpen, minimized, onOpen, onClose]);

  // ===== 初始化 3D 角色浮层（替代 Fab）=====
  useEffect(() => {
    if (agentRef.current) return; // 避免重复初始化
    const modelUrl =
      (process.env.PUBLIC_URL || '') + '/models/tucanProfessor_merged.fbx';

    const STATES = ['idle', 'thinking', 'walking'];
    const BASE_W = 286;      // 220 × 1.3
    const BASE_H = 312;      // 240 × 1.3
    const MODAL_W = 560;
    const MODAL_H = 600;
    const MIN_W = 340;
    const MIN_H = 380;
    const MAX_W = 900;
    const MAX_H = 960;

    const agent = new AlphaAgent({
      modelUrl,
      width: BASE_W,
      height: BASE_H,
      position: { left: 40, bottom: 60 },
      draggable: true,
      closable: true,
      interactive: true,
      statusVisible: true,
      popupEnabled: true,
      popupTitle: 'AI Assistant',
      enterLeaveAnimation: true,
      persistHidden: true,
      storageKey: 'chat_assistant_3d_hidden',
      states: {
        idle:     { text: 'At your service...', dot: '#94a3b8' },
        thinking: { text: 'Thinking...',         dot: '#f59e0b' },
        walking:  { text: 'Processing...',       dot: '#3b82f6' },
      },
    });
    agentRef.current = agent;

    const overlay = agent._dom?.overlay;
    if (overlay) {
      overlay.style.transition = [
        'width 280ms cubic-bezier(.22,.9,.3,1)',
        'height 280ms cubic-bezier(.22,.9,.3,1)',
        'left 280ms cubic-bezier(.22,.9,.3,1)',
        'top 280ms cubic-bezier(.22,.9,.3,1)',
        'right 280ms cubic-bezier(.22,.9,.3,1)',
        'bottom 280ms cubic-bezier(.22,.9,.3,1)',
        'transform 280ms cubic-bezier(.22,.9,.3,1)',
      ].join(', ');
      overlay.style.willChange = 'width, height, left, top, right, bottom, transform';
    }

    // ===== 相机/视角重置辅助 =====
    const resetCameraView = () => {
      const sm = agent._sceneManager;
      if (!sm || !sm.camera) return;
      sm.camera.position.set(0, 3, 6);
      sm.camera.lookAt(0, 1.5, 0);
      sm._controlsTarget && sm._controlsTarget.set(0, 1.5, 0);
      if (sm._spherical && typeof sm._updateCameraFromSpherical === 'function') {
        sm._spherical.setFromVector3(sm.camera.position.clone().sub(sm._controlsTarget));
      }
    };

    // ===== 把浮层移到右下角（与原 Fab 位置一致）=====
    const moveToBottomRight = () => {
      const ov = agent._dom?.overlay;
      if (!ov) return;
      if (modalStateRef.current.isOpen) return; // 弹层模式下不动
      ov.style.transform = 'none';
      ov.style.left = 'auto';
      ov.style.top = 'auto';
      ov.style.right = '24px';
      ov.style.bottom = '24px';
    };
    moveToBottomRight();
    window.addEventListener('resize', moveToBottomRight);

    // ===== 统一的点击逻辑：读 ref 的最新值 =====
    const toggleChat = () => {
      const s = chatUiStateRef.current;
      if (!s.isOpen) {
        if (s.onOpen) s.onOpen();
        setMinimized(false);
      } else if (s.minimized) {
        setMinimized(false);
      } else {
        setMinimized(true);
      }
    };

    // ===== 弹层位置（left/top）设置：不用 translate 居中，改为绝对坐标（方便拖动时修改）=====
    const setModalPosition = (w, h, rect) => {
      const ov = agent._dom?.overlay;
      if (!ov) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left, top;
      if (rect) {
        left = rect.left;
        top = rect.top;
      } else {
        left = Math.max(16, Math.floor((vw - w) / 2));
        top = Math.max(16, Math.floor((vh - h) / 2));
      }
      // clamp 可视区
      left = Math.min(vw - Math.min(120, w), left);
      top = Math.min(vh - Math.min(80, h), top);
      ov.style.transform = 'none';
      ov.style.left = left + 'px';
      ov.style.top = top + 'px';
      ov.style.right = 'auto';
      ov.style.bottom = 'auto';
      ov.style.width = w + 'px';
      ov.style.height = h + 'px';
      ov.style.zIndex = '10001';
    };

    // ===== 注册：小浮层模式（默认）的按钮 =====
    const registerCompactActions = () => {
      agent.clearActions();
      agent.registerAction({
        id: 'switch-state',
        label: 'Next Pose',
        icon: '🔄',
        callback: () => {
          const cur = agent.getState() || 'idle';
          const idx = STATES.indexOf(cur);
          const next = STATES[(idx + 1) % STATES.length];
          agent.setState(next);
        },
      });
      agent.registerAction({
        id: 'expand',
        label: 'Expand View',
        icon: '🔍',
        style: {
          backgroundColor: 'rgba(139, 92, 246, 0.13)',
          color: '#7c3aed',
          fontWeight: '600',
        },
        callback: () => openModal(),
      });
    };

    // ===== 注册：弹层模式（大）的按钮（移除 ➕➖ 改手势）=====
    const registerModalActions = () => {
      agent.clearActions();
      // —— 视角控制组（单按钮：重置视角）——
      agent.registerAction({
        id: 'reset-view',
        label: 'Reset View',
        icon: '🎯',
        callback: () => resetCameraView(),
      });
      // —— 动作切换组 ——
      agent.registerAction({
        id: 'act-idle',
        label: 'Idle',
        icon: '💤',
        callback: () => agent.setState('idle'),
      });
      agent.registerAction({
        id: 'act-thinking',
        label: 'Think',
        icon: '🧠',
        style: {
          backgroundColor: 'rgba(245,158,11,0.13)',
          color: '#b45309',
        },
        callback: () => agent.setState('thinking'),
      });
      agent.registerAction({
        id: 'act-walking',
        label: 'Work',
        icon: '🚶',
        style: {
          backgroundColor: 'rgba(59,130,246,0.13)',
          color: '#1d4ed8',
        },
        callback: () => agent.setState('walking'),
      });
      // —— 关闭弹层 ——
      agent.registerAction({
        id: 'collapse',
        label: 'Close',
        icon: '✕',
        style: {
          backgroundColor: 'rgba(239,68,68,0.13)',
          color: '#b91c1c',
          fontWeight: '700',
        },
        callback: () => closeModal(),
      });
    };

    // ===== 弹层专用控件：右下角拖拽 resize 把手 + 顶部拖拽条（移动弹层）=====
    let modalResizeHandle = null;
    let modalDragBar = null;
    let resizeState = null;   // { startX, startY, startW, startH, startLeft, startTop }
    let dragState = null;     // { startX, startY, startLeft, startTop }
    let wheelResizeHandler = null; // Ctrl/Cmd + 滚轮 缩放弹层

    const createModalHandles = () => {
      const ov = agent._dom?.overlay;
      if (!ov) return;
      // —— 顶部拖拽条（约 26px，用来移动弹层）——
      modalDragBar = document.createElement('div');
      Object.assign(modalDragBar.style, {
        position: 'absolute',
        left: '0',
        right: '0',
        top: '0',
        height: '26px',
        cursor: 'grab',
        zIndex: '20',
        background: 'linear-gradient(180deg, rgba(124,58,237,0.22) 0%, rgba(124,58,237,0) 100%)',
        borderRadius: '24px 24px 0 0',
        userSelect: 'none',
      });
      modalDragBar.title = 'Drag to move the viewer';
      modalDragBar.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        const ov = agent._dom?.overlay;
        if (!ov) return;
        const rect = ov.getBoundingClientRect();
        dragState = {
          startX: e.clientX,
          startY: e.clientY,
          startLeft: rect.left,
          startTop: rect.top,
        };
        modalDragBar.style.cursor = 'grabbing';
      });
      ov.appendChild(modalDragBar);

      // —— 右下角 resize 把手 ——
      modalResizeHandle = document.createElement('div');
      Object.assign(modalResizeHandle.style, {
        position: 'absolute',
        right: '2px',
        bottom: '2px',
        width: '22px',
        height: '22px',
        cursor: 'nwse-resize',
        zIndex: '21',
        display: 'grid',
        placeItems: 'center',
        userSelect: 'none',
      });
      // 画一个对角线小三角（resize 指示）
      const diag = document.createElement('div');
      diag.innerHTML = '⤡';
      Object.assign(diag.style, {
        color: 'rgba(124,58,237,0.55)',
        fontSize: '14px',
        fontWeight: '700',
        transform: 'rotate(45deg)',
      });
      modalResizeHandle.appendChild(diag);
      modalResizeHandle.title = 'Drag to resize (or hold Ctrl/⌘ + scroll)';
      modalResizeHandle.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        const ov = agent._dom?.overlay;
        if (!ov) return;
        const rect = ov.getBoundingClientRect();
        const st = modalStateRef.current;
        resizeState = {
          startX: e.clientX,
          startY: e.clientY,
          startW: rect.width,
          startH: rect.height,
        };
        st.currentSize = { width: rect.width, height: rect.height };
      });
      ov.appendChild(modalResizeHandle);

      // —— Ctrl/Meta + 滚轮 = 调整弹层尺寸 ——
      wheelResizeHandler = (e) => {
        if (!modalStateRef.current.isOpen) return;
        const canvasWrap = agent._dom?.canvasWrap;
        const inCanvas = canvasWrap && (
          e.clientX >= canvasWrap.getBoundingClientRect().left &&
          e.clientX <= canvasWrap.getBoundingClientRect().right &&
          e.clientY >= canvasWrap.getBoundingClientRect().top &&
          e.clientY <= canvasWrap.getBoundingClientRect().bottom
        );
        // 鼠标在弹层（overlay）内，且按住 Ctrl/Cmd：resize 弹层
        const ov = agent._dom?.overlay;
        const inOverlay = ov && (
          e.clientX >= ov.getBoundingClientRect().left &&
          e.clientX <= ov.getBoundingClientRect().right &&
          e.clientY >= ov.getBoundingClientRect().top &&
          e.clientY <= ov.getBoundingClientRect().bottom
        );
        if (!(e.ctrlKey || e.metaKey)) return; // 只有 Ctrl/Meta 才 resize 弹层
        if (!inOverlay && !inCanvas) return;
        e.preventDefault();
        const factor = e.deltaY > 0 ? 1.05 : 0.95;
        const st = modalStateRef.current;
        const nextW = Math.max(MIN_W, Math.min(MAX_W, st.currentSize.width * factor));
        const nextH = Math.max(MIN_H, Math.min(MAX_H, st.currentSize.height * factor));
        const rw = Math.round(nextW);
        const rh = Math.round(nextH);
        st.currentSize = { width: rw, height: rh };
        const ov2 = agent._dom?.overlay;
        if (ov2) {
          const oldRect = ov2.getBoundingClientRect();
          const cx = oldRect.left + oldRect.width / 2;
          const cy = oldRect.top + oldRect.height / 2;
          setModalPosition(rw, rh, {
            left: Math.round(cx - rw / 2),
            top: Math.round(cy - rh / 2),
          });
        }
        agent.setSize(rw, rh);
      };
      document.addEventListener('wheel', wheelResizeHandler, { passive: false });

      // —— mousemove / mouseup：处理 drag 条移动 & resize 把手 ——
      const mm = (e) => {
        if (dragState) {
          const ov = agent._dom?.overlay;
          if (!ov) return;
          const dx = e.clientX - dragState.startX;
          const dy = e.clientY - dragState.startY;
          const st = modalStateRef.current;
          const rw = st.currentSize.width;
          const rh = st.currentSize.height;
          setModalPosition(rw, rh, {
            left: dragState.startLeft + dx,
            top: dragState.startTop + dy,
          });
        }
        if (resizeState) {
          const dx = e.clientX - resizeState.startX;
          const dy = e.clientY - resizeState.startY;
          const nextW = Math.max(MIN_W, Math.min(MAX_W, resizeState.startW + dx));
          const nextH = Math.max(MIN_H, Math.min(MAX_H, resizeState.startH + dy));
          const rw = Math.round(nextW);
          const rh = Math.round(nextH);
          modalStateRef.current.currentSize = { width: rw, height: rh };
          const ov = agent._dom?.overlay;
          if (ov) {
            ov.style.width = rw + 'px';
            ov.style.height = rh + 'px';
          }
          agent.setSize(rw, rh);
        }
      };
      const mu = () => {
        if (dragState) {
          dragState = null;
          if (modalDragBar) modalDragBar.style.cursor = 'grab';
        }
        resizeState = null;
      };
      document.addEventListener('mousemove', mm);
      document.addEventListener('mouseup', mu);
      // 存下来 close 时清理
      modalStateRef.current._cleanupHandles = () => {
        document.removeEventListener('mousemove', mm);
        document.removeEventListener('mouseup', mu);
      };
    };

    const destroyModalHandles = () => {
      modalStateRef.current._cleanupHandles?.();
      modalStateRef.current._cleanupHandles = null;
      if (wheelResizeHandler) {
        document.removeEventListener('wheel', wheelResizeHandler);
        wheelResizeHandler = null;
      }
      if (modalResizeHandle && modalResizeHandle.parentNode) {
        modalResizeHandle.parentNode.removeChild(modalResizeHandle);
      }
      modalResizeHandle = null;
      if (modalDragBar && modalDragBar.parentNode) {
        modalDragBar.parentNode.removeChild(modalDragBar);
      }
      modalDragBar = null;
    };

    // ===== 创建半透明黑色遮罩 =====
    const createBackdrop = () => {
      const bd = document.createElement('div');
      bd.style.cssText = [
        'position: fixed',
        'inset: 0',
        'background: rgba(15, 23, 42, 0.45)',
        'backdrop-filter: blur(2px)',
        '-webkit-backdrop-filter: blur(2px)',
        'z-index: 10000',
        'opacity: 0',
        'transition: opacity 220ms ease',
      ].join(';');
      document.body.appendChild(bd);
      requestAnimationFrame(() => { bd.style.opacity = '1'; });
      return bd;
    };

    // ===== ESC 键关闭弹层 =====
    const bindKeydown = () => {
      const handler = (e) => {
        if (e.key === 'Escape' && modalStateRef.current.isOpen) {
          closeModal();
        }
      };
      document.addEventListener('keydown', handler);
      return handler;
    };

    // ===== 暂停动作自动循环（弹层打开时，用户自己切动作更自由）=====
    const pauseStateLoop = () => {
      if (stateLoopTimerRef.current) {
        clearTimeout(stateLoopTimerRef.current);
        stateLoopTimerRef.current = null;
      }
    };

    // ===== 打开弹层 =====
    const openModal = () => {
      const st = modalStateRef.current;
      if (st.isOpen) return;
      st.isOpen = true;
      st.pausedLoop = true;
      pauseStateLoop();

      st.originalSize = agent.getSize() || { width: BASE_W, height: BASE_H };
      st.currentSize = { width: MODAL_W, height: MODAL_H };

      // 1. 先创建遮罩（背景点击不关闭，避免误触；用按钮或 ESC 关）
      st.backdrop = createBackdrop();

      // 2. 居中显示
      setModalPosition(MODAL_W, MODAL_H);
      agent.setSize(MODAL_W, MODAL_H);

      // 3. 创建 resize 把手 + 顶部 drag 条
      createModalHandles();

      // 4. 切换按钮组
      registerModalActions();

      // 5. ESC 绑定
      st.keydownHandler = bindKeydown();

      // 6. 状态条：提示手势
      agent.setStatusText('LMB:Rotate · RMB:Pan · MMB/Scroll:Zoom · Top bar:Move · Ctrl/⌘+Scroll:Resize');
      agent.setStatusColor('#7c3aed');
    };

    // ===== 关闭弹层 =====
    const closeModal = () => {
      const st = modalStateRef.current;
      if (!st.isOpen) return;
      st.isOpen = false;

      // 1. 移除 ESC 监听 & handles
      if (st.keydownHandler) {
        document.removeEventListener('keydown', st.keydownHandler);
        st.keydownHandler = null;
      }
      destroyModalHandles();

      // 2. 移除遮罩（淡出后再 remove）
      if (st.backdrop) {
        const bd = st.backdrop;
        bd.style.opacity = '0';
        setTimeout(() => { if (bd.parentNode) bd.parentNode.removeChild(bd); }, 240);
        st.backdrop = null;
      }

      // 3. 还原尺寸 & 位置
      const { width: rw, height: rh } = st.originalSize;
      st.currentSize = { ...st.originalSize };
      agent.setSize(rw, rh);
      const ov = agent._dom?.overlay;
      if (ov) {
        ov.style.width = rw + 'px';
        ov.style.height = rh + 'px';
        ov.style.zIndex = '';
      }
      moveToBottomRight();

      // 4. 还原按钮组
      registerCompactActions();

      // 5. 恢复动作循环（从当前状态继续）
      st.pausedLoop = false;
      const cur = agent.getState() || 'idle';
      const startIdx = Math.max(0, STATES.indexOf(cur));
      scheduleNext(startIdx + 1);
    };

    // 初次注册：默认小浮层按钮
    registerCompactActions();

    // ===== 点击 3D 画布本身 → 打开/最小化聊天（小浮层模式下）；弹层模式不触发 =====
    const canvasWrap = agent._dom?.canvasWrap;
    if (canvasWrap) {
      canvasWrap.addEventListener('click', (e) => {
        e.stopPropagation();
        if (modalStateRef.current.isOpen) return; // 弹层里的 3D 画布点击只用来旋转，不触发聊天
        toggleChat();
      });
    }

    // ===== 动作自动交替： idle(6s) → thinking(5s) → walking(5s) → 循环 =====
    const scheduleNext = (stateIdx) => {
      if (!agentRef.current) return;
      if (modalStateRef.current.isOpen || modalStateRef.current.pausedLoop) return;
      const durations = [6000, 5000, 5000];
      const nextState = STATES[stateIdx % STATES.length];
      agent.setState(nextState).catch(() => {});
      stateLoopTimerRef.current = setTimeout(() => {
        scheduleNext(stateIdx + 1);
      }, durations[stateIdx % durations.length]);
    };

    // 进场动画内部已处理「正脸朝屏幕 + 由小到大（相机 radius+模型 scale+外层尺寸三重缓动）+ walking→idle」
    // 这里只需要在进场完成后，启动状态自动循环
    agent.on('show', () => {
      // 进场动画结束后的最终相机正脸校准（确保 idle 后角度正好）
      if (typeof resetCameraView === 'function') resetCameraView();
      if (modalStateRef.current.isOpen || modalStateRef.current.pausedLoop) return;
      scheduleNext(0);
    });
    // modelLoaded 不做任何事（交给 enterAnimation 跑 1.8s）
    // agent.on('modelLoaded', ...) REMOVED

    return () => {
      window.removeEventListener('resize', moveToBottomRight);
      if (stateLoopTimerRef.current) {
        clearTimeout(stateLoopTimerRef.current);
        stateLoopTimerRef.current = null;
      }
      // 清理弹层残留
      const st = modalStateRef.current;
      if (st.keydownHandler) document.removeEventListener('keydown', st.keydownHandler);
      destroyModalHandles();
      if (st.backdrop && st.backdrop.parentNode) st.backdrop.parentNode.removeChild(st.backdrop);
      st.isOpen = false;
      st.backdrop = null;
      st.keydownHandler = null;
      st.pausedLoop = false;
      agent.destroy();
      agentRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当 isOpen / minimized 变化时，同步 3D 状态（切 thinking 增加代入感）
  useEffect(() => {
    const agent = agentRef.current;
    if (!agent) return;
    if (isOpen && !minimized) {
      // 打开聊天窗口：切到 thinking 几秒后回到循环
      agent.setState('thinking').catch(() => {});
    }
  }, [isOpen, minimized]);
  ★ 3D 角色浮层代码结束 */

  // 图标浮层（Fab）：打开 / 最小化聊天
  const handleFabClick = () => {
    if (!isOpen) {
      if (onOpen) onOpen();
      setMinimized(false);
    } else {
      setMinimized((v) => !v);
    }
  };

  // 初始化窗口尺寸和位置
  useEffect(() => {
    if (isOpen && windowSize.width === 0) {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const initialWidth = Math.floor(screenWidth * 0.75);
      const initialHeight = Math.floor(screenHeight * 0.75);
      const initialX = Math.floor((screenWidth - initialWidth) / 2);
      const initialY = Math.floor((screenHeight - initialHeight) / 2);
      
      setWindowSize({ width: initialWidth, height: initialHeight });
      setWindowPosition({ x: initialX, y: initialY });
    }
  }, [isOpen, windowSize.width]);

  // 滚动控制
  const scrollToBottom = useCallback(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    const near = isNearBottom(el, 2);
    setAtBottom(near);
    if (autoScrollEnabled && near) {
      el.scrollTop = el.scrollHeight;
    }
  }, [autoScrollEnabled]);

  const handleScroll = useCallback(() => {
    const el = chatBodyRef.current;
    const near = isNearBottom(el, 2);
    setAtBottom(near);
    setAutoScrollEnabled(near);
  }, []);

  // 拖拽处理函数
  const handleMouseDown = useCallback((e) => {
    if (e.target === headerRef.current || headerRef.current?.contains(e.target)) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - windowPosition.x,
        y: e.clientY - windowPosition.y
      });
    }
  }, [windowPosition]);

  const handleResizeMouseDown = useCallback((e) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: windowSize.width,
      height: windowSize.height
    });
  }, [windowSize]);

  // 全局鼠标移动和释放事件
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        
        // 限制在屏幕范围内
        const maxX = window.innerWidth - windowSize.width;
        const maxY = window.innerHeight - windowSize.height;
        
        setWindowPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        
        const newWidth = Math.max(400, Math.min(window.innerWidth - windowPosition.x, resizeStart.width + deltaX));
        const newHeight = Math.max(300, Math.min(window.innerHeight - windowPosition.y, resizeStart.height + deltaY));
        
        setWindowSize({ width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, resizeStart, windowSize, windowPosition]);

  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // 会话 ID
  const getStorageKey = useCallback(async () => {
    const user = await Auth.currentAuthenticatedUser();
    const sub = user.attributes.sub;
    const qid = questionObj?.id || 'global';
    return `chat:conv:${sub}:${qid}`;
  }, [questionObj?.id]);

  const ensureConversationId = useCallback(async () => {
    if (convIdRef.current) return convIdRef.current;
    const key = await getStorageKey();
    let cid = localStorage.getItem(key);
    if (!cid) {
      cid = uuidv4();
      localStorage.setItem(key, cid);
    }
    convIdRef.current = cid;
    return cid;
  }, [getStorageKey]);

  // 拉历史
  const loadHistory = useCallback(async (cid) => {
    const data = await callGraphQL(MESSAGES_BY_CONV, { conversationId: cid, limit: 200 });
    const messages = data?.messagesByConversation?.items || [];
    setMessages(messages);
    
    // 计算用户消息数量
    const userCount = messages.filter(msg => msg.role === 'user').length;
    setUserMessageCount(userCount);
    
    return messages; // 返回消息列表，方便调用者检查
  }, []);

  // 轮询
  const stopPolling = () => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = null;
  };
  const startPolling = (cid) => {
    stopPolling();
    pollTimer.current = setInterval(async () => {
      try {
        const data = await callGraphQL(MESSAGES_BY_CONV, { conversationId: cid, limit: 200 });
        const serverItems = data?.messagesByConversation?.items || [];
        
        // 检查是否有 STREAMING 状态的消息
        const hasStreaming = serverItems.some(msg => msg.status === 'STREAMING');
        
        if (sendingRef.current) {
          // 发送期间先缓存，等发送结束再一次性落地，避免覆盖本地乐观项
          pendingServerSnapRef.current = serverItems;
        } else {
          setMessages(serverItems);
          // 更新用户消息计数
          const userCount = serverItems.filter(msg => msg.role === 'user').length;
          setUserMessageCount(userCount);
        }
        
        // 如果没有 STREAMING 状态的消息，且没有正在发送的消息，停止轮询
        if (!hasStreaming && !sendingRef.current) {
          stopPolling();
        }
      } catch {
        // 错误时也停止轮询，避免无限重试
        stopPolling();
      }
    }, 1000);
  };

  // 首问 + 背景
  const firstPrompt = useMemo(() => `Hi, I need help with this question.`, []);
  const backgroundInfo = useMemo(() => {
    const correct = getCorrect?.() || '';
    const wrongs = (getWrongs?.() || []).join(', ');
    const title = stripHtml(questionObj?.title || '');
    const rationale = stripHtml(questionObj?.answer?.rationale || '');
    const selectedAnswerText = selectedAnswer ? `Selected answer: ${selectedAnswer}` : '';

    const rows = [];
    rows.push(`Question: ${title}`);
    if (questionObj?.answer?.answerType === 'SINGLE_CORRECT' && questionObj?.answer?.choices) {
      const choicesText = questionObj.answer.choices
        .map((choice, idx) => `${String.fromCharCode(65 + idx)}. ${stripHtml(choice)}`)
        .join('\n');
      rows.push(`Answer choices:\n${choicesText}`);
    }
    if (correct) rows.push(`Correct answer: ${correct}`);
    if (selectedAnswerText) rows.push(selectedAnswerText);
    if (wrongs) rows.push(`My wrong answer(s): ${wrongs}`);
    if (rationale) rows.push(`Original explanation (if any): ${rationale}`);
    if (passage?.body) rows.push(`Passage body: ${stripHtml(passage.body)}`);
    if (passage?.attribution) rows.push(`Passage attribution: ${stripHtml(passage.attribution)}`);
    if (passage?.direction) rows.push(`Passage direction: ${stripHtml(passage.direction)}`);
    return rows.join('\n');
  }, [questionObj, getCorrect, getWrongs, selectedAnswer, passage]);

  // 跟踪上一次的 autoSendFirstMessage 值
  const prevAutoSendRef = useRef(false);

  // 组件挂载时查询是否已有聊天记录（仅用于提前判断，打开弹窗时会重新加载确认）
  useEffect(() => {
    // 题目ID变化时，先重置状态和会话ID
    hasHistoryMessagesRef.current = false;
    convIdRef.current = null; // 重置会话ID，确保使用新的题目对应的会话
    
    (async () => {
      try {
        const cid = await ensureConversationId();
        const data = await callGraphQL(MESSAGES_BY_CONV, { conversationId: cid, limit: 1 });
        const messages = data?.messagesByConversation?.items || [];
        // 如果有任何消息记录，标记为已有历史消息
        hasHistoryMessagesRef.current = messages.length > 0;
        
        // 调试日志（开发环境）
        if (process.env.NODE_ENV !== 'production') {
          console.log('[ChatAssistant] Mount check history:', {
            conversationId: cid,
            messagesCount: messages.length,
            hasHistoryMessages: hasHistoryMessagesRef.current,
          });
        }
      } catch (error) {
        // 查询失败时，假设没有历史消息
        hasHistoryMessagesRef.current = false;
        if (process.env.NODE_ENV !== 'production') {
          console.error('[ChatAssistant] Mount check history error:', error);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionObj?.id]); // 当题目ID变化时重新查询

  // 打开弹窗：拉历史 + 轮询 + 可选首问
  useEffect(() => {
    if (!isOpen) {
      stopPolling();
      return;
    }
    (async () => {
      const cid = await ensureConversationId();
      const historyMessages = await loadHistory(cid);
      startPolling(cid);

      // 更新历史消息状态（打开弹窗时，以实际加载的消息为准）
      const hasUserMessages = historyMessages.some(msg => msg.role === 'user');
      // 明确更新历史消息状态：如果有消息就设为 true，如果没有消息就设为 false
      hasHistoryMessagesRef.current = historyMessages.length > 0;

      // 检查是否需要自动发送：
      // 1. 历史消息中没有用户消息（避免重复发送）
      // 2. 且历史消息列表为空（确保没有历史记录）
      // 3. 且（autoSendFirstMessage 为 true 或首次打开弹窗）
      // 注意：首次打开弹窗时，即使 autoSendFirstMessage 为 false，如果没有历史消息也应该自动发送
      const shouldAutoSend = !hasUserMessages && 
        historyMessages.length === 0 &&
        (autoSendFirstMessage || firstOpen.current);

      // 调试日志（开发环境）
      if (process.env.NODE_ENV !== 'production') {
        console.log('[ChatAssistant] Auto-send check:', {
          autoSendFirstMessage,
          hasUserMessages,
          historyMessagesLength: historyMessages.length,
          hasHistoryMessagesRef: hasHistoryMessagesRef.current,
          firstOpen: firstOpen.current,
          prevAutoSend: prevAutoSendRef.current,
          shouldAutoSend,
        });
      }

      if (shouldAutoSend) {
        firstOpen.current = false;
        prevAutoSendRef.current = autoSendFirstMessage;
        sendingRef.current = true;
        const sendingTimeout = setTimeout(() => (sendingRef.current = false), 2500); // 兜底

        const localAsstId = `local-asst-${Date.now()}`;
        const localUser = {
          id: `local-user-${Date.now() - 1}`,
          role: 'user',
          content: firstPrompt,
          status: 'COMPLETED',
          createdAt: ts(),
          updatedAt: ts(),
          conversationId: cid,
        };
        const localAsst = {
          id: localAsstId,
          role: 'assistant',
          content: '…',
          status: 'STREAMING',
          createdAt: ts(),
          updatedAt: ts(),
          conversationId: cid,
        };
        setMessages((prev) => [...prev, localUser, localAsst]);
        setDisplayedMessages((prev) => [...prev, localUser, localAsst]);
        setAutoScrollEnabled(true);
        scrollToBottom();

        callGraphQL(SEND_MESSAGE, {
          conversationId: cid,
          content: firstPrompt,
          background: backgroundInfo,
        })
          .then((d) => {
            const ret = d?.sendMessage;
            if (ret?.id) {
              setMessages((prev) =>
                prev.map((m) => (m.id === localAsstId ? { ...ret, content: '…' } : m)),
              );
            }
          })
          .catch(() => {})
          .finally(() => {
            sendingRef.current = false;
            clearTimeout(sendingTimeout);
            // 如果发送期间轮询回来的快照被缓存了，这里一次性落地
            if (pendingServerSnapRef.current) {
              setMessages(pendingServerSnapRef.current);
              pendingServerSnapRef.current = null;
            }
          });
      } else if (firstOpen.current) {
        firstOpen.current = false;
      }
      
      // 更新 prevAutoSendRef
      prevAutoSendRef.current = autoSendFirstMessage;
    })();

    return () => {
      stopPolling();
      if (typingTimerRef.current) clearInterval(typingTimerRef.current);
      if (firstTokenTimerRef.current) clearTimeout(firstTokenTimerRef.current);
      typingTimerRef.current = null;
      firstTokenTimerRef.current = null;
      shownTextRef.current = '';
      targetTextRef.current = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoSendFirstMessage]);

  // —— 只对“最后一条 assistant”打字机，其余即时展示 —— //
  useEffect(() => {
    if (!messages.length) {
      setDisplayedMessages([]);
      setTypingMsgId(null);
      shownTextRef.current = '';
      targetTextRef.current = '';
      if (typingTimerRef.current) clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
      return;
    }

    const last = messages[messages.length - 1];
    const isAssistant = last?.role === 'assistant';
    const head = messages.slice(0, -1);

    if (!isAssistant) {
      setDisplayedMessages(messages);
      setTypingMsgId(null);
      shownTextRef.current = '';
      targetTextRef.current = '';
      if (typingTimerRef.current) clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
      return;
    }

    const id = last.id;
    const target = last.content || '';
    targetTextRef.current = target;

    const switching = typingMsgId !== id;
    if (switching) {
      setTypingMsgId(id);
      shownTextRef.current = '';
      targetTextRef.current = target; // ☆ 明确记录当前目标，防 race
      if (typingTimerRef.current) clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
      if (firstTokenTimerRef.current) clearTimeout(firstTokenTimerRef.current);

      setDisplayedMessages([...head, { ...last, content: '' }]);
      firstTokenTimerRef.current = setTimeout(() => startTypewriter(id), FIRST_TOKEN_DELAY_MS);
      return;
    }

    // 同一条，继续追进度
    setDisplayedMessages([...head, { ...last, content: shownTextRef.current }]);
    if (!typingTimerRef.current) startTypewriter(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, typingMsgId]);

  const startTypewriter = (id) => {
    if (typingTimerRef.current) clearInterval(typingTimerRef.current);
    const stepMs = 25;
    const stepChars = Math.max(1, Math.round((PACE_CPS / 1000) * stepMs));

    typingTimerRef.current = setInterval(() => {
      const full = targetTextRef.current || '';
      const shown = shownTextRef.current || '';
      if (shown.length >= full.length) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        return;
      }
      const nextLen = Math.min(full.length, shown.length + stepChars);
      shownTextRef.current = full.slice(0, nextLen);

      setDisplayedMessages((prev) => {
        if (!prev.length) return prev;
        const lastIdx = prev.length - 1;
        const last = prev[lastIdx];
        if (last.id !== id || last.role !== 'assistant') return prev;
        const next = prev.slice();
        next[lastIdx] = { ...last, content: shownTextRef.current };
        return next;
      });

      scrollToBottom();
    }, stepMs);
  };

  // displayedMessages 变更时，若允许则滚到底
  useEffect(() => {
    scrollToBottom();
  }, [displayedMessages, scrollToBottom]);

  // 发送
  const onSend = useCallback(async () => {
    const text = input.trim();
    if (!text || userMessageCount >= 20) return;
    
    setUserMessageCount(prev => prev + 1);
    setInput('');
    setAutoScrollEnabled(true);
    sendingRef.current = true;
    const sendingTimeout = setTimeout(() => (sendingRef.current = false), 2500);

    const cid = await ensureConversationId();

    const localAsstId = `local-asst-${Date.now()}`;
    const localUser = {
      id: `local-user-${Date.now() - 1}`,
      role: 'user',
      content: text,
      status: 'COMPLETED',
      createdAt: ts(),
      updatedAt: ts(),
      conversationId: cid,
    };
    const localAsst = {
      id: localAsstId,
      role: 'assistant',
      content: '…',
      status: 'STREAMING',
      createdAt: ts(),
      updatedAt: ts(),
      conversationId: cid,
    };

    setMessages((prev) => [...prev, localUser, localAsst]);
    setDisplayedMessages((prev) => [...prev, localUser, localAsst]);
    scrollToBottom();

    // 重新启动轮询，确保能获取AI回复
    startPolling(cid);

    callGraphQL(SEND_MESSAGE, { conversationId: cid, content: text, background: backgroundInfo })
      .then((d) => {
        const ret = d?.sendMessage;
        if (ret?.id) {
          setMessages((prev) =>
            prev.map((m) => (m.id === localAsstId ? { ...ret, content: '…' } : m)),
          );
        }
      })
      .catch(() => {})
      .finally(() => {
        sendingRef.current = false;
        clearTimeout(sendingTimeout);
        if (pendingServerSnapRef.current) {
          setMessages(pendingServerSnapRef.current);
          pendingServerSnapRef.current = null;
        }
      });
  }, [input, userMessageCount, ensureConversationId, scrollToBottom, backgroundInfo]);

  const quicks = [
    `Why is my answer incorrect?`,
    `Give me one more example of the same concept.`,
    `Explain step-by-step and highlight key traps.`,
  ];

  return (
    <>
      <Fab
        onClick={handleFabClick}
        title={isOpen && !minimized ? 'Minimize' : 'Open chat'}
      >
        💬
      </Fab>

      {isOpen && !minimized && (
        <Panel
          ref={panelRef}
          style={{
            width: `${windowSize.width}px`,
            height: `${windowSize.height}px`,
            left: `${windowPosition.x}px`,
            top: `${windowPosition.y}px`,
            position: 'fixed'
          }}
        >
          <Header
            ref={headerRef}
            onMouseDown={handleMouseDown}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <Title>AI Assistant</Title>
            <HeaderRight>
              <SmallBtn onClick={() => setMinimized(true)}>Minimize</SmallBtn>
              <CloseBtn onClick={onClose}>✕</CloseBtn>
            </HeaderRight>
          </Header>

          <QuickBar>
            {quicks.map((q) => (
              <QuickBtn key={q} onClick={() => setInput(q)}>
                {q}
              </QuickBtn>
            ))}
          </QuickBar>

          <Body ref={chatBodyRef}>
            {displayedMessages.map((m) => (
              <Bubble key={m.id} $me={m.role === 'user'}>
                <BubbleHeader>
                  <Avatar $me={m.role === 'user'}>
                    {m.role === 'user' ? '🧑' : <AvatarImage src={toocanImage} alt="AI Assistant" />}
                  </Avatar>
                  <Role $me={m.role === 'user'}>{m.role}</Role>
                  <Time>
                    {new Date(m.updatedAt || m.createdAt || Date.now()).toLocaleTimeString()}
                  </Time>
                  {m.role === 'assistant' && m.status === 'STREAMING' && <Typing>thinking...</Typing>}
                </BubbleHeader>
                <BubbleContent>
                  <MdTight>
                    <Markdown>
                      {m.role === 'assistant' && m.status === 'FAILED'
                        ? FAILED_ASSISTANT_MESSAGE
                        : m.content || ' '}
                    </Markdown>
                  </MdTight>
                </BubbleContent>
                {!(m.role === 'assistant' && m.status === 'FAILED') && (
                  <BubbleTools>
                    <CopyBtn onClick={() => navigator.clipboard?.writeText(m.content || '')}>
                      Copy
                    </CopyBtn>
                  </BubbleTools>
                )}
              </Bubble>
            ))}
          </Body>

          <Footer>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={userMessageCount >= 20 ? "Chat limit reached" : "Type your question…"}
              onKeyDown={(e) => e.key === 'Enter' && onSend()}
              disabled={userMessageCount >= 20}
            />
            <Send onClick={onSend} disabled={userMessageCount >= 20}>Send</Send>
          </Footer>

          {/* ★ 回到底部按钮：用户不在底部就显示 */}
          {showScrollBtn && (
            <ScrollIndicator
              onClick={() => {
                setAutoScrollEnabled(true);
                const el = chatBodyRef.current;
                if (el) el.scrollTop = el.scrollHeight;
              }}
            >
              <span>⬇︎</span> Scroll to bottom
            </ScrollIndicator>
          )}
          
          {/* 调整大小手柄 */}
          <ResizeHandle
            ref={resizeHandleRef}
            onMouseDown={handleResizeMouseDown}
          />
        </Panel>
      )}
    </>
  );
}

// ===== 样式 =====
const Fab = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: #4ea2e2;
  color: #fff;
  font-size: 22px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1001;
`;
const Panel = styled.div`
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
  min-width: 400px;
  min-height: 300px;
  user-select: none;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${toocanImage});
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.1;
    z-index: 1011;
    pointer-events: none;
  }
  
  & > * {
    position: relative;
    z-index: 2;
  }
`;
const Header = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fafafa;
`;
const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
`;
const HeaderRight = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const SmallBtn = styled.button`
  border: 1px solid #ddd;
  background: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
`;
const CloseBtn = styled.button`
  border: none;
  background: transparent;
  font-size: 20px;
  cursor: pointer;
`;
const QuickBar = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #f2f2f2;
  flex-wrap: wrap;
`;
const QuickBtn = styled.button`
  border: 1px solid #ddd;
  background: #f7f9fc;
  border-radius: 16px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background: #eef3fb;
  }
`;
const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
`;
const Bubble = styled.div`
  align-self: ${(p) => (p.$me ? 'flex-end' : 'flex-start')};
  background: ${(p) => (p.$me ? '#e7f1ff' : '#f7f7f7')};
  padding: 8px 10px;
  border-radius: 10px;
  max-width: 90%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;
const BubbleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
`;
const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${(p) => (p.$me ? '#cfe0ff' : '#eaeaea')};
  font-size: 16px;
  overflow: hidden;
`;
const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;
const Role = styled.div`
  text-transform: uppercase;
  font-size: 14px;
  letter-spacing: 0.04em;
  opacity: ${(p) => (p.$me ? 0.75 : 0.65)};
`;
const Time = styled.div`
  font-size: 14px;
  opacity: 0.45;
  margin-left: auto;
`;
const Typing = styled.div`
  font-size: 14px;
  color: #4ea2e2;
  margin-left: 10px;
`;

/* 这里统一收紧 Markdown 基本排版，包括列表与公式块 */
const BubbleContent = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: #2b2b2b;
  word-break: break-word;

  /* 段落更紧凑 */
  p {
    margin: 0 0 6px 0;
  }

  /* 列表：去除过大的默认外边距，收紧行距 */

  /* 列表整体更紧凑 */
  ul,
  ol {
    margin: 4px 0 6px;
    padding-left: 1.2em;
  }
  li {
    margin: 2px 0;
  }
  /* 关键：去掉 li 里 p 的额外外边距，避免“气泡”之间大空行 */
  li > p {
    margin: 0;
  }
  /* 代码块上/下外边距适中 */
  pre {
    margin: 6px 0;
  }

  /* KaTeX：块级公式与行级公式的间距微调 */
  .katex {
    line-height: 1.5;
  }
  .katex-display {
    margin: 6px 0;
  } /* 默认 KaTeX 比较大，这里收紧 */
`;
// 收紧 Markdown 与 KaTeX 的默认留白
const MdTight = styled.div`
  /* 仅段落保留换行；列表恢复 normal，避免把 \n 渲染成空行 */
  p {
    white-space: pre-wrap;
  }
  ul,
  ol,
  li {
    white-space: normal;
  }
  /* 段落更紧凑 */
  & p {
    margin: 0 0 6px 0;
    line-height: 1.5;
  }

  /* 列表整体：减少上下 margin，控制缩进 */
  & ul,
  & ol {
    margin: 6px 0; /* 默认 1em 太大，改成 6px */
    padding-left: 18px; /* 控制缩进宽度 */
  }

  /* 列表项：减小项间距；li 内如果包了 p，要去掉 p 的 margin */
  & li {
    margin: 2px 0;
    line-height: 1.5;
  }
  & li > p {
    margin: 0;
  } /* 关键：消除 li 内 p 的额外留白 */

  /* 嵌套列表再收紧一点 */
  & ul ul,
  & ol ul,
  & ul ol,
  & ol ol {
    margin: 4px 0;
    padding-left: 16px;
  }

  /* 段落后接列表时别再多一个大空隙 */
  & p + ul,
  & p + ol {
    margin-top: 4px;
  }

  /* KaTeX：缩小 display 公式上下留白，并让行内字号自然 */
  & .katex {
    font-size: 1em;
  }
  & .katex-display {
    margin: 6px 0;
  } /* 默认 ~1em，改成 6px */
  & .katex-display > .katex {
    display: inline-block;
  }

  /* 代码块与行内代码的留白 */
  & pre {
    margin: 6px 0;
  }
  & code {
    padding: 0 3px;
  }

  /* 引用块更紧凑 */
  & blockquote {
    margin: 6px 0;
    padding-left: 10px;
    border-left: 3px solid #eee;
  }

  /* 表格也统一成紧凑间距（你已有，但这里兜底） */
  & table {
    margin: 6px 0;
  }
`;

const BubbleTools = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
`;
const CopyBtn = styled.button`
  border: none;
  background: transparent;
  font-size: 14px;
  color: #7a7a7a;
  cursor: pointer;
  &:hover {
    color: #111;
  }
`;
const Footer = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid #eee;
  background: #fafafa;
`;
const Input = styled.input`
  flex: 1;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #ddd;
  outline: none;
  font-size: 16px;
  &:focus {
    border-color: #4ea2e2;
    box-shadow: 0 0 0 3px rgba(78, 162, 226, 0.15);
  }
  &:disabled {
    background-color: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }
`;
const Send = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  background: #4ea2e2;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;
const P = styled.p`
  margin: 0 0 6px 0;
`;
const CodeInline = styled.code`
  background: #f3f3f3;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
`;
const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  margin: 6px 0;
`;
const Th = styled.th`
  text-align: left;
  border-bottom: 1px solid #ddd;
  padding: 6px 8px;
  background: #fafafa;
`;
const Td = styled.td`
  border-bottom: 1px solid #eee;
  padding: 4px 8px;
`;
const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 60px;
  right: 20px;
  background: #4ea2e2;
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 1001;
  user-select: none;
  &:hover {
    background: #3d8bc7;
  }
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  cursor: nw-resize;
  background: linear-gradient(-45deg, transparent 0%, transparent 30%, #ccc 30%, #ccc 35%, transparent 35%, transparent 65%, #ccc 65%, #ccc 70%, transparent 70%);
  z-index: 1002;
  
  &:hover {
    background: linear-gradient(-45deg, transparent 0%, transparent 30%, #999 30%, #999 35%, transparent 35%, transparent 65%, #999 65%, #999 70%, transparent 70%);
  }
`;
